// Montagem (pura) da tabela do PDF exportado pelos grids. Sem jsPDF/React aqui
// para poder testar isolado — quem desenha o PDF é util/gridPdfExport.js.
import { formatValue } from "./gridFormatValue";

// Colunas de botão (editar/excluir/ações) não têm dado para imprimir.
const ACTION_FIELDS = new Set([
  "edit",
  "editar",
  "action",
  "actions",
  "acoes",
  "delete",
  "upimages",
]);

export const isPrintableColumn = (col) => {
  if (!col || !col.field) return false;
  if (col.type === "actions") return false;
  if (String(col.field).startsWith("__")) return false;
  if (ACTION_FIELDS.has(String(col.field).toLowerCase())) return false;
  const header = String(col.headerName ?? "").trim();
  return header !== "";
};

// As colunas de foto só renderizam um <Avatar> com o caminho do arquivo; o
// campo é que diz que é imagem ("avatar", "categoryImage"...). "updateImages"
// e "countImages" são data/número e não casam.
const IMAGE_FIELD_EXACT = /^(avatar|img|image|imagem|foto|photo|thumb|thumbnail|logo)$/i;
const IMAGE_FIELD_SUFFIX = /[a-z](Image|Img|Avatar|Photo|Foto|Logo|Thumb)$/;

export const isImageColumn = (col) => {
  if (!col) return false;
  if (col.image === true) return true;
  if (col.image === false) return false;
  const field = String(col.field ?? "");
  return IMAGE_FIELD_EXACT.test(field) || IMAGE_FIELD_SUFFIX.test(field);
};

// Caminho da imagem na célula: string, ou o primeiro item de uma lista.
export const imagePathOf = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return imagePathOf(value[0]);
  if (typeof value === "object") return imagePathOf(value.url ?? value.img ?? value.src);
  const s = String(value).trim();
  return s || null;
};

export const formatPdfCell = (value, format) => {
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (value instanceof Date) return formatValue(value, format || "dd/MM/yyyy");
  if (typeof value === "object") return "";
  return String(formatValue(value, format));
};

// columns: [{ field, headerName, format?, dbField?, getValue?(row), getRawValue?(row) }]
// Retorna { head, body, images, columns }: body tem "" nas células de imagem e
// images[r][c] guarda o caminho da foto (null quando não é imagem/vazia).
export const buildPdfTable = (columns, rows) => {
  const cols = (columns || [])
    .filter(isPrintableColumn)
    .map((c) => ({ ...c, isImage: isImageColumn(c) }));
  const head = [cols.map((c) => c.headerName)];
  const body = [];
  const images = [];
  (rows || []).forEach((row) => {
    const line = [];
    const imgs = [];
    cols.forEach((c) => {
      const raw = c.getRawValue ? c.getRawValue(row) : row?.[c.dbField ?? c.field];
      if (c.isImage) {
        line.push("");
        imgs.push(imagePathOf(raw));
        return;
      }
      const value = c.getValue ? c.getValue(row) : raw;
      line.push(formatPdfCell(value, c.format));
      imgs.push(null);
    });
    body.push(line);
    images.push(imgs);
  });
  return { head, body, images, columns: cols };
};

// Fonte encolhe conforme a quantidade de colunas para caber na largura da página.
export const pdfFontSize = (columnCount, orientation) => {
  const extra = orientation === "landscape" ? 4 : 0;
  if (columnCount <= 6 + extra) return 9;
  if (columnCount <= 10 + extra) return 8;
  if (columnCount <= 14 + extra) return 7;
  return 6;
};

export const normalizeOrientation = (orientation) =>
  orientation === "landscape" ? "landscape" : "portrait";

const SAMPLE_ROWS = 300;

// Larguras das colunas a partir do conteúdo, para o cabeçalho não quebrar
// letra a letra (o autoTable divide a página por igual e espreme as colunas).
// measure(text) → largura em pt na fonte atual; padding = soma esquerda+direita.
// Mínimo de cada coluna = maior palavra do cabeçalho (ou o conteúdo, se curto);
// se nem os mínimos cabem, fits=false e o chamador quebra colunas em páginas.
export const computeColumnWidths = ({
  head,
  body,
  columns,
  measure,
  available,
  padding = 6,
  imageWidth = 34,
  maxWidth = 220,
}) => {
  const headers = head?.[0] || [];
  const sample = (body || []).slice(0, SAMPLE_ROWS);
  const mins = [];
  const naturals = [];

  headers.forEach((header, i) => {
    if (columns?.[i]?.isImage) {
      const w = Math.max(imageWidth, measure(String(header)) + padding);
      mins.push(w);
      naturals.push(w);
      return;
    }
    const words = String(header).split(/\s+/).filter(Boolean);
    const longestWord = Math.max(0, ...words.map((w) => measure(w))) + padding;
    const headerFull = measure(String(header)) + padding;
    const content = Math.max(0, ...sample.map((r) => measure(String(r[i] ?? "")))) + padding;
    const natural = Math.min(maxWidth, Math.max(headerFull, content));
    const min = Math.min(natural, Math.max(longestWord, Math.min(content, 60)));
    mins.push(min);
    naturals.push(Math.max(min, natural));
  });

  const sum = (arr) => arr.reduce((a, b) => a + b, 0);
  const sumNatural = sum(naturals);
  const sumMin = sum(mins);

  if (sumNatural <= available) {
    // Sobra espaço: distribui entre as colunas de texto (imagem fica fixa).
    const textNatural = sum(naturals.filter((_, i) => !columns?.[i]?.isImage));
    const extra = available - sumNatural;
    const widths = naturals.map((w, i) =>
      columns?.[i]?.isImage || !textNatural ? w : w + (extra * w) / textNatural,
    );
    return { widths, fits: true };
  }
  if (sumMin <= available) {
    const k = (available - sumMin) / (sumNatural - sumMin);
    return { widths: naturals.map((n, i) => mins[i] + (n - mins[i]) * k), fits: true };
  }
  return { widths: mins, fits: false };
};

// A origem do Spaces leva ~1s por foto; o CDN do mesmo bucket ~0,1s e também
// responde CORS. A miniatura do PDF busca sempre pelo CDN.
const SPACES_ORIGIN = /^https:\/\/([a-z0-9-]+)\.([a-z0-9]+)\.digitaloceanspaces\.com\//i;

export const toCdnUrl = (url) => {
  if (!url) return url;
  return String(url).replace(SPACES_ORIGIN, "https://$1.$2.cdn.digitaloceanspaces.com/");
};
